import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './RegisterPage.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // Step management - 4 steps including review
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Form data - PERSISTENT across all steps
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    date_of_birth: '',
    address: '',
    occupation: '',
    membership_category: '',
    primary_member_id: '',
    relationship: ''
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  
  // Validation states for real-time checking
  const [emailValid, setEmailValid] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailFormatValid, setEmailFormatValid] = useState(null);
  const [phoneValid, setPhoneValid] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  
  // Domain validation states
  const [domainValid, setDomainValid] = useState(null);
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainSuggestion, setDomainSuggestion] = useState('');
  
  // Evidence file for associate members
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceFileName, setEvidenceFileName] = useState('');
  
  // Prefix preview for relationship selection
  const [prefixPreview, setPrefixPreview] = useState('');
  
  // Primary member search
  const [primaryMemberSearch, setPrimaryMemberSearch] = useState('');
  const [primaryMemberResults, setPrimaryMemberResults] = useState([]);
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false);
  const [primaryMemberLoading, setPrimaryMemberLoading] = useState(false);
  const [selectedPrimaryMember, setSelectedPrimaryMember] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeout = useRef(null);
  const primarySearchRef = useRef(null);
  
  // Progress percentage
  const progressPercentage = (currentStep / totalSteps) * 100;

  // ALL MEMBERSHIP CATEGORIES including Spouse Member
  const membershipCategories = [
    { value: 'full_member', label: 'Full Member', prefix: 'FUL', requiresPrimary: false,
      description: 'NLNG, NSML, and RAIS Direct Staff and Secondees based in Bonny Island',
      note: 'Full access to all club facilities and events' },
    { value: 'spouse_member', label: 'Spouse Member', prefix: 'SPE', requiresPrimary: true,
      description: 'Spouse of a Full Member',
      note: 'Spouses receive SPE prefix but have full member privileges with their own login credentials' },
    { value: 'junior_member', label: 'Junior Member', prefix: 'CHL', requiresPrimary: true,
      description: 'Children aged 12-18 years whose parents are full members of The Club',
      note: 'Must be registered by a parent who is a full member. Will receive CHL prefix.' },
    { value: 'family_member', label: 'Family Member', prefix: 'VAR', requiresPrimary: true,
      description: 'Parents or siblings of primary members (NOT spouse)',
      note: 'Select relationship below. Prefix varies by relationship.' },
    { value: 'honorary_member', label: 'Honorary Member', prefix: 'HON', requiresPrimary: false,
      description: 'Renewable one-year memberships for deserving personalities',
      note: 'Limited to five per year. Requires RCEC approval' },
    { value: 'associate_member', label: 'Associate Member', prefix: 'ASC', requiresPrimary: false,
      description: 'Employees of JV Oil/Gas Companies or major NLNG contractors on Bonny Island',
      note: 'Requires management application and verifiable employment evidence' },
    { value: 'non_bonny_staff', label: 'Non-Bonny Staff', prefix: 'NBS', requiresPrimary: false,
      description: 'NLNG staff visiting from other locations',
      note: 'Cannot become members but may use facilities upon presenting a valid ID card' }
  ];

  // Relationship options for Family Members
  const relationshipOptions = [
    { value: 'spouse', label: 'Spouse', prefix: 'SPE', description: 'Husband or wife of primary member', icon: 'fa-heart' },
    { value: 'child', label: 'Child', prefix: 'CHL', description: 'Son or daughter of primary member', icon: 'fa-child' },
    { value: 'parent', label: 'Parent', prefix: 'PAR', description: 'Father or mother of primary member', icon: 'fa-users' },
    { value: 'sibling', label: 'Sibling', prefix: 'SIB', description: 'Brother or sister of primary member', icon: 'fa-user-friends' },
    { value: 'other', label: 'Other Family', prefix: 'DEP', description: 'Other family relationship', icon: 'fa-user' }
  ];

  // Get current category
  const currentCategory = membershipCategories.find(c => c.value === formData.membership_category);
  const requiresPrimaryMember = currentCategory?.requiresPrimary || false;
  
  // Get selected relationship info
  const selectedRelationship = relationshipOptions.find(r => r.value === formData.relationship);
  
  // Update prefix preview when relationship changes
  useEffect(() => {
    // Handle Spouse Member (SPE prefix with full member status)
    if (formData.membership_category === 'spouse_member') {
        setPrefixPreview('SPE (Spouse Member - Full Member)');
    } 
    // Handle Junior Member
    else if (formData.membership_category === 'junior_member') {
        setPrefixPreview('CHL (Child Member)');
    } 
    // Handle Family Member with relationship selection
    else if (formData.membership_category === 'family_member' && selectedRelationship) {
        setPrefixPreview(`${selectedRelationship.prefix} (${selectedRelationship.label})`);
    } 
    // Handle categories that don't require a primary member (Full, Honorary, Associate, Non-Bonny Staff)
    else if (formData.membership_category && !requiresPrimaryMember && formData.membership_category !== 'spouse_member') {
        const category = membershipCategories.find(c => c.value === formData.membership_category);
        setPrefixPreview(`${category?.prefix} (${category?.label})`);
    } 
    // Handle Family Member when no relationship selected yet
    else if (formData.membership_category === 'family_member' && !selectedRelationship) {
        setPrefixPreview('Select relationship to see prefix');
    }
    // Clear preview for other cases
    else {
        setPrefixPreview('');
    }
  }, [formData.membership_category, formData.relationship, selectedRelationship, requiresPrimaryMember]);

  // Function to check basic email format
  const isValidEmailFormat = (email) => {
    if (!email) return false;
    const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Function to check if email domain is valid
  const isValidEmailDomain = (email) => {
    if (!email) return false;
    
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    const validDomains = [
      'gmail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com', 'rocketmail.com',
      'hotmail.com', 'outlook.com', 'live.com', 'msn.com', 'aol.com', 'mail.com', 
      'protonmail.com', 'proton.me', 'zoho.com', 'icloud.com', 'me.com', 'mac.com',
      'yandex.com', 'yandex.ru', 'gmx.com', 'gmx.net', 'nlng.com', 'nlng.com.ng', 
      'nnpcgroup.com', 'nnpc.com', 'shell.com', 'total.com', 'exxonmobil.com', 'chevron.com',
      'agip.com', 'seplat.com', 'firstbanknigeria.com', 'gtbank.com', 'accessbankplc.com',
      'zenithbank.com', 'ubagroup.com', 'nigerianavy.mil.ng', 'nigerianarmy.mil.ng',
      'nigerianairforce.mil.ng', 'uniport.edu.ng', 'uniuyo.edu.ng', 'uniben.edu', 
      'unn.edu.ng', 'oauife.edu.ng'
    ];
    
    if (validDomains.includes(domain)) return true;
    if (domain.endsWith('.edu.ng') || domain.endsWith('.gov.ng') || 
        domain.endsWith('.org.ng') || domain.endsWith('.sch.ng')) return true;
    
    const domainPattern = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]\.[a-z]{2,}(\.[a-z]{2,})?$/i;
    if (domainPattern.test(domain)) return true;
    
    return false;
  };

  // Enhanced email validation function - checks format AND domain
  const validateEmailCompletely = (email) => {
    if (!email) return { valid: false, reason: 'Email is required' };
    
    const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return { valid: false, reason: 'Invalid email format' };
    }
    
    const domain = email.split('@')[1].toLowerCase();
    
    const disposableDomains = [
      'tempmail.com', '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
      'throwawaymail.com', 'yopmail.com', 'temp-mail.org', 'fakeinbox.com',
      'trashmail.com', 'spambox.us', 'mailnator.com', 'getairmail.com'
    ];
    
    if (disposableDomains.includes(domain)) {
      return { valid: false, reason: 'Disposable email addresses are not allowed' };
    }
    
    const domainPattern = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9](\.[a-z]{2,})+$/i;
    if (!domainPattern.test(domain)) {
      return { valid: false, reason: 'Invalid email domain' };
    }
    
    return { valid: true, reason: null };
  };

  // Function to check if email is complete enough to validate
  const isEmailComplete = (email) => {
    if (!email) return false;
    if (email.length < 5) return false;
    if (!email.includes('@')) return false;
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    if (parts[0].length < 1) return false;
    if (parts[1].length < 3) return false;
    if (!parts[1].includes('.')) return false;
    return true;
  };

  // Close primary dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (primarySearchRef.current && !primarySearchRef.current.contains(event.target)) {
        setShowPrimaryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsPageLoaded(true);
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('registration_form_data');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({ ...prev, ...parsed }));
        if (parsed.primary_member_id) {
          setSelectedPrimaryMember({ id: parsed.primary_member_id, first_name: 'Selected', last_name: 'Parent' });
        }
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (formData.first_name || formData.email) {
      localStorage.setItem('registration_form_data', JSON.stringify(formData));
    }
  }, [formData]);

  // DIRECT API CALLS
  const API_BASE_URL = 'https://nlngraclub.com/nlnapi/index.php';

  // Check email availability via API
  const checkEmailAvailability = useCallback(async (email) => {
    if (!isEmailComplete(email)) {
      setEmailValid(null);
      return;
    }
    
    const formatValidation = validateEmailCompletely(email);
    if (!formatValidation.valid) {
      setEmailFormatValid(false);
      setEmailValid(null);
      return;
    }
    
    setEmailFormatValid(true);
    setEmailChecking(true);
    
    try {
      const url = `${API_BASE_URL}?url=check-email&_=${Date.now()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: email })
      });
      const responseText = await response.text();
      
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse.trim());
      }
      
      setEmailValid(!data.exists);
    } catch (error) {
      console.error('Email check error:', error);
      setEmailValid(null);
    } finally {
      setEmailChecking(false);
    }
  }, []);

  // Check domain validity via backend MX records
  const checkEmailDomain = useCallback(async (email) => {
    if (!isEmailComplete(email)) {
      setDomainValid(null);
      setDomainSuggestion('');
      return;
    }
    
    const domain = email.split('@')[1].toLowerCase();
    
    const validDomainsList = [
      'gmail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com', 'rocketmail.com',
      'hotmail.com', 'outlook.com', 'live.com', 'msn.com', 'aol.com', 'mail.com',
      'protonmail.com', 'proton.me', 'zoho.com', 'icloud.com', 'me.com', 'mac.com',
      'yandex.com', 'yandex.ru', 'gmx.com', 'gmx.net', 'nlng.com', 'nlng.com.ng',
      'nnpcgroup.com', 'nnpc.com', 'shell.com', 'total.com', 'exxonmobil.com', 'chevron.com',
      'agip.com', 'seplat.com', 'firstbanknigeria.com', 'gtbank.com', 'accessbankplc.com',
      'zenithbank.com', 'ubagroup.com', 'nigerianavy.mil.ng', 'nigerianarmy.mil.ng',
      'nigerianairforce.mil.ng', 'uniport.edu.ng', 'uniuyo.edu.ng', 'uniben.edu',
      'unn.edu.ng', 'oauife.edu.ng'
    ];
    
    if (validDomainsList.includes(domain)) {
      setDomainValid(true);
      setDomainSuggestion('');
      return;
    }
    
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'nlng.com'];
    
    for (const common of commonDomains) {
      if (domain === common) continue;
      
      const commonBase = common.split('.')[0];
      const domainBase = domain.split('.')[0];
      
      if (domainBase === 'yahoo' && commonBase === 'yahoo') {
        continue;
      }
      
      if (domainBase.length >= 3 && commonBase.length >= 3) {
        if (commonBase.includes(domainBase) && domainBase.length >= commonBase.length - 2) {
          setDomainSuggestion(common);
          return;
        }
        if (domainBase.includes(commonBase) && commonBase.length >= domainBase.length - 2) {
          setDomainSuggestion(common);
          return;
        }
      }
    }
    
    setDomainSuggestion('');
    
    if (isValidEmailDomain(email)) {
      setDomainValid(true);
      return;
    }
    
    setDomainChecking(true);
    try {
      const url = `${API_BASE_URL}?url=validate-email-domain&_=${Date.now()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: email })
      });
      
      const responseText = await response.text();
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let result;
      if (jsonMatch) {
        result = JSON.parse(jsonMatch[0]);
      } else {
        result = JSON.parse(cleanResponse.trim());
      }
      
      setDomainValid(result.valid);
    } catch (error) {
      console.error('Domain validation error:', error);
      setDomainValid(isValidEmailDomain(email));
    } finally {
      setDomainChecking(false);
    }
  }, []);

  // Check phone availability using direct API
  const checkPhoneAvailability = useCallback(async (phone) => {
    if (!phone || phone.length < 5) {
      setPhoneValid(null);
      return;
    }
    
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const internationalPhoneRegex = /^(\+?[0-9]{1,4}[0-9]{5,15}|[0-9]{8,15})$/;
    
    if (!internationalPhoneRegex.test(cleanPhone)) {
      setPhoneValid(false);
      return;
    }
    
    setPhoneChecking(true);
    
    try {
      const url = `${API_BASE_URL}?url=check-phone&_=${Date.now()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone })
      });
      
      const responseText = await response.text();
      
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data;
      
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        data = JSON.parse(cleanResponse.trim());
      }
      
      setPhoneValid(!data.exists);
      
    } catch (error) {
      console.error('Phone check error:', error);
      setPhoneValid(null);
    } finally {
      setPhoneChecking(false);
    }
  }, []);

  // Search primary members using direct API
// Search primary members using direct API - IMPROVED VERSION
const searchPrimaryMembers = useCallback(async (searchTerm) => {
  if (selectedPrimaryMember && selectedPrimaryMember.id) return;
  
  if (!searchTerm || searchTerm.length < 2) {
    setPrimaryMemberResults([]);
    setShowPrimaryDropdown(false);
    setHasSearched(false);
    return;
  }
  
  setPrimaryMemberLoading(true);
  setHasSearched(true);
  
  try {
    // First, try the debug endpoint to check if member exists
    console.log(`🔍 Debug checking for: ${searchTerm}`);
    const debugUrl = `${API_BASE_URL}?url=debug-check-member&_=${Date.now()}`;
    const debugResponse = await fetch(debugUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ name: searchTerm })
    });
    const debugText = await debugResponse.text();
    console.log('📥 Debug response:', debugText);
    
    let debugData;
    try {
      let cleanDebug = debugText;
      cleanDebug = cleanDebug.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanDebug = cleanDebug.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      const debugJsonMatch = cleanDebug.match(/\{[\s\S]*\}/);
      if (debugJsonMatch) {
        debugData = JSON.parse(debugJsonMatch[0]);
      } else {
        debugData = JSON.parse(cleanDebug.trim());
      }
      console.log('✅ Debug data:', debugData);
    } catch (e) {
      console.warn('Debug endpoint not available, proceeding with search');
    }
    
    // Now do the actual search
    const url = `${API_BASE_URL}?url=search-primary-members&_=${Date.now()}`;
    console.log(`🔍 Searching for: ${searchTerm}`);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ 
        search: searchTerm,
        exclude_id: 0 // Don't exclude anyone for registration
      })
    });
    
    const responseText = await response.text();
    console.log('📥 Raw search response:', responseText.substring(0, 500));
    
    let cleanResponse = responseText;
    cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
    
    const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
    let data;
    if (jsonMatch) {
      data = JSON.parse(jsonMatch[0]);
    } else {
      data = JSON.parse(cleanResponse.trim());
    }
    
    console.log('✅ Parsed search results:', data);
    
    if (data.success && data.members && data.members.length > 0) {
      setPrimaryMemberResults(data.members);
      setShowPrimaryDropdown(true);
    } else {
      setPrimaryMemberResults([]);
      setShowPrimaryDropdown(true);
      console.log('No members found for search:', searchTerm);
    }
  } catch (error) {
    console.error('❌ Primary member search error:', error);
    setPrimaryMemberResults([]);
    setShowPrimaryDropdown(true);
  } finally {
    setPrimaryMemberLoading(false);
  }
}, [selectedPrimaryMember]);
  // Debounced search
// Debounced search - IMPROVED VERSION
useEffect(() => {
  if (searchTimeout.current) clearTimeout(searchTimeout.current);
  
  // Don't search if we already have a selected member
  if (selectedPrimaryMember && selectedPrimaryMember.id) {
    setPrimaryMemberResults([]);
    setShowPrimaryDropdown(false);
    return;
  }
  
  // Only search if we need a primary member and have a search term
  if (requiresPrimaryMember && primaryMemberSearch && primaryMemberSearch.length >= 2) {
    searchTimeout.current = setTimeout(() => {
      searchPrimaryMembers(primaryMemberSearch);
    }, 500);
  } else if (!primaryMemberSearch || primaryMemberSearch.length < 2) {
    setPrimaryMemberResults([]);
    setShowPrimaryDropdown(false);
    setHasSearched(false);
  }
  
  return () => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
  };
}, [primaryMemberSearch, requiresPrimaryMember, searchPrimaryMembers, selectedPrimaryMember]);

  // Debounced email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email && isEmailComplete(formData.email)) {
        const formatValidation = validateEmailCompletely(formData.email);
        if (formatValidation.valid) {
          setEmailFormatValid(true);
          checkEmailAvailability(formData.email);
          checkEmailDomain(formData.email);
        } else {
          setEmailFormatValid(false);
          setEmailValid(null);
          setDomainValid(null);
        }
      } else if (formData.email && !isEmailComplete(formData.email)) {
        setEmailFormatValid(null);
        setEmailValid(null);
        setDomainValid(null);
        setDomainSuggestion('');
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.email, checkEmailAvailability, checkEmailDomain]);

  // Debounced phone check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.phone && formData.phone.length >= 10) {
        checkPhoneAvailability(formData.phone);
      } else {
        setPhoneValid(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.phone, checkPhoneAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (message) { setMessage(''); setIsError(false); }
  };
// Fallback: Manual search through all members
const manualSearchPrimaryMembers = useCallback(async (searchTerm) => {
  if (selectedPrimaryMember && selectedPrimaryMember.id) return;
  if (!searchTerm || searchTerm.length < 2) {
    setPrimaryMemberResults([]);
    setShowPrimaryDropdown(false);
    return;
  }
  
  setPrimaryMemberLoading(true);
  setHasSearched(true);
  
  try {
    // Try to get all members and filter locally
    const url = `${API_BASE_URL}?url=all-users&_=${Date.now()}`;
    const token = localStorage.getItem('nlngra_jwt');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    
    const data = await response.json();
    
    if (data.success && data.users) {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.users.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const reverseName = `${user.last_name} ${user.first_name}`.toLowerCase();
        const membershipNum = user.membership_number?.toLowerCase() || '';
        
        return fullName.includes(searchLower) || 
               reverseName.includes(searchLower) || 
               membershipNum.includes(searchLower);
      });
      
      setPrimaryMemberResults(filtered);
      setShowPrimaryDropdown(true);
    }
  } catch (error) {
    console.error('Manual search error:', error);
  } finally {
    setPrimaryMemberLoading(false);
  }
}, [selectedPrimaryMember]);
  const handleCategoryChange = (e) => {
    const selectedValue = e.target.value;
    const selectedCategory = membershipCategories.find(cat => cat.value === selectedValue);
    
    // Clear primary member if new category doesn't require it
    if (selectedCategory && !selectedCategory.requiresPrimary && requiresPrimaryMember) {
        clearSelectedPrimaryMember();
    }
    
    // Clear relationship if not family member (spouse doesn't use relationship field)
    if (selectedValue !== 'family_member') {
        setFormData(prev => ({ ...prev, relationship: '' }));
    }
    
    if (selectedValue !== 'associate_member') {
        setEvidenceFile(null);
        setEvidenceFileName('');
    }
    
    setFormData(prev => ({ ...prev, membership_category: selectedValue }));
  };

  const handleEvidenceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setMessage('Please upload a PDF, JPG, or PNG file');
        setIsError(true);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage('File size must be less than 5MB');
        setIsError(true);
        return;
      }
      setEvidenceFile(file);
      setEvidenceFileName(file.name);
      setMessage('');
      setIsError(false);
    }
  };

  const selectPrimaryMember = (member) => {
    console.log('Selected member:', member);
    setSelectedPrimaryMember(member);
    setFormData(prev => ({ ...prev, primary_member_id: member.id }));
    setPrimaryMemberSearch(`${member.first_name} ${member.last_name} (${member.membership_number})`);
    setShowPrimaryDropdown(false);
    setHasSearched(false);
    setPrimaryMemberResults([]);
    const updatedData = { ...formData, primary_member_id: member.id };
    localStorage.setItem('registration_form_data', JSON.stringify(updatedData));
  };

  const clearSelectedPrimaryMember = () => {
    setSelectedPrimaryMember(null);
    setFormData(prev => ({ ...prev, primary_member_id: '' }));
    setPrimaryMemberSearch('');
    setShowPrimaryDropdown(false);
    setHasSearched(false);
    setPrimaryMemberResults([]);
    const updatedData = { ...formData, primary_member_id: '' };
    localStorage.setItem('registration_form_data', JSON.stringify(updatedData));
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.first_name.trim()) {
        setMessage('First name is required'); setIsError(true); return false;
      }
      if (!formData.last_name.trim()) {
        setMessage('Last name is required'); setIsError(true); return false;
      }
      if (!formData.email.trim()) {
        setMessage('Email address is required'); setIsError(true); return false;
      }
      if (!isEmailComplete(formData.email)) {
        setMessage('Please enter a complete email address (e.g., name@example.com)'); setIsError(true); return false;
      }
      if (emailFormatValid === false) {
        setMessage('Please enter a valid email address (e.g., name@example.com)'); setIsError(true); return false;
      }
      if (emailValid === false) {
        setMessage('This email is already registered. Please use a different email.'); setIsError(true); return false;
      }
      if (domainValid === false) {
        setMessage('This email domain is invalid or cannot receive emails. Please use a valid email address.'); setIsError(true); return false;
      }
      
      if (!formData.phone.trim()) {
        setMessage('Phone number is required'); setIsError(true); return false;
      }
      
      const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      const internationalPhoneRegex = /^(\+?[0-9]{1,4}[0-9]{5,15}|[0-9]{8,15})$/;
      if (!internationalPhoneRegex.test(cleanPhone)) {
        setMessage('Please enter a valid phone number with country code (e.g., +1234567890)'); 
        setIsError(true); 
        return false;
      }
      
      if (phoneValid === false) {
        setMessage('This phone number is already registered.'); setIsError(true); return false;
      }
      if (!formData.membership_category) {
        setMessage('Please select a membership category'); setIsError(true); return false;
      }
      
      // Handle spouse member validation with custom message
      if (requiresPrimaryMember && !formData.primary_member_id) {
        if (formData.membership_category === 'spouse_member') {
          setMessage('Please select your spouse/partner (primary member) to verify your relationship');
        } else {
          setMessage('Please select a primary member (parent/guardian)');
        }
        setIsError(true); 
        return false;
      }
      
      // Relationship validation - ONLY for family_member (spouse uses separate field)
      if (formData.membership_category === 'family_member' && !formData.relationship) {
        setMessage('Please select your relationship to the primary member'); 
        setIsError(true); 
        return false;
      }
      
      if (formData.membership_category === 'associate_member' && !evidenceFile) {
        setMessage('Please upload your supporting evidence document (PDF or image)'); 
        setIsError(true); 
        return false;
      }
      return true;
    }
    
    if (currentStep === 2) {
      return true;
    }
    
    if (currentStep === 3) {
      if (!formData.password) {
        setMessage('Password is required'); setIsError(true); return false;
      }
      if (formData.password.length < 6) {
        setMessage('Password must be at least 6 characters'); setIsError(true); return false;
      }
      if (formData.password !== formData.confirm_password) {
        setMessage('Passwords do not match'); setIsError(true); return false;
      }
      if (!acceptedTerms) {
        setMessage('You must accept the Terms of Service and Privacy Policy'); setIsError(true); return false;
      }
      return true;
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    
    setLoading(true);
    setMessage('');
    setIsError(false);

    const formDataToSend = new FormData();
    
    // Handle relationship value based on membership category
    let relationshipValue = null;
    if (formData.membership_category === 'spouse_member') {
      relationshipValue = 'spouse';
    } else if (formData.membership_category === 'junior_member') {
      relationshipValue = 'child';
    } else {
      relationshipValue = formData.relationship;
    }
    
    const registrationPayload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim().replace(/\s/g, ''),
      password: formData.password,
      membership_category: formData.membership_category,
      primary_member_id: formData.primary_member_id || null,
      date_of_birth: formData.date_of_birth || null,
      address: formData.address.trim() || '',
      occupation: formData.occupation.trim() || '',
      relationship: relationshipValue
    };

    formDataToSend.append('member_data', JSON.stringify(registrationPayload));
    
    if (formData.membership_category === 'associate_member' && evidenceFile) {
      formDataToSend.append('evidence', evidenceFile);
    }

    console.log('Submitting registration with payload:', registrationPayload);

    try {
      const url = 'https://nlngraclub.com/nlnapi/index.php?url=register';
      console.log('Sending request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        body: formDataToSend
      });

      console.log('Response status:', response.status);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let result;
      try {
        let cleanedResponse = responseText;
        cleanedResponse = cleanedResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
        cleanedResponse = cleanedResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
        cleanedResponse = cleanedResponse.replace(/Cannot modify header information.*?\n/g, '');
        
        const jsonMatch = cleanedResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          result = JSON.parse(jsonMatch[0]);
        } else {
          result = JSON.parse(cleanedResponse.trim());
        }
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Raw response that failed to parse:', responseText);
        
        if (responseText.includes('Unknown column') || responseText.includes('primary_member_id')) {
          setMessage('Database configuration error. Please contact support.');
        } else if (responseText.includes('syntax error')) {
          setMessage('Server configuration error. Please contact support.');
        } else {
          setMessage('Server returned an invalid response. Please try again.');
        }
        setIsError(true);
        setLoading(false);
        return;
      }
      
      console.log('Parsed result:', result);
      
      if (result.success) {
        localStorage.removeItem('registration_form_data');
        
        let successMessage = result.message || 'Registration successful! Please check your email to activate your account.';
        
        if (result.prefix) {
          successMessage += ` Your membership prefix is ${result.prefix}.`;
        }
        
        setMessage(successMessage);
        setIsError(false);
        
        setFormData({
          first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: '',
          date_of_birth: '', address: '', occupation: '', membership_category: '', primary_member_id: '', relationship: ''
        });
        setAcceptedTerms(false);
        setSelectedPrimaryMember(null);
        setPrimaryMemberSearch('');
        setEvidenceFile(null);
        setEvidenceFileName('');
        
        setTimeout(() => navigate('/login', { 
          state: { registrationSuccess: true, email: registrationPayload.email } 
        }), 4000);
      } else {
        let errorMsg = result.message || result.error || 'Registration failed. Please try again.';
        
        if (errorMsg.toLowerCase().includes('email already')) {
          errorMsg = 'This email is already registered. Please use a different email.';
        } else if (errorMsg.toLowerCase().includes('phone already')) {
          errorMsg = 'This phone number is already registered.';
        }
        
        setMessage(errorMsg);
        setIsError(true);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setMessage('Network error. Please check your connection and try again.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryDescription = (value) => membershipCategories.find(cat => cat.value === value)?.description || '';
  const getCategoryNote = (value) => membershipCategories.find(cat => cat.value === value)?.note || '';
  const getCategoryLabel = (value) => membershipCategories.find(cat => cat.value === value)?.label || '';

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 24 } }
  };

  // Review Step Component
  const ReviewStep = () => {
    const categoryLabel = getCategoryLabel(formData.membership_category);
    const categoryDesc = getCategoryDescription(formData.membership_category);
    const relationshipInfo = relationshipOptions.find(r => r.value === formData.relationship);
    
    return (
      <div className="review-step">
        <h3 className="review-title"><i className="fas fa-clipboard-list"></i> Review Your Information</h3>
        <p className="review-subtitle">Please verify all details before submitting</p>
        
        <div className="review-sections">
          <div className="review-section">
            <div className="review-section-header">
              <i className="fas fa-user-circle"></i>
              <h4>Account Information</h4>
              <button type="button" className="review-edit-btn" onClick={() => setCurrentStep(1)}>
                <i className="fas fa-edit"></i> Edit
              </button>
            </div>
            <div className="review-grid">
              <div className="review-item"><strong>First Name:</strong> <span>{formData.first_name || '—'}</span></div>
              <div className="review-item"><strong>Last Name:</strong> <span>{formData.last_name || '—'}</span></div>
              <div className="review-item"><strong>Email:</strong> <span>{formData.email || '—'}</span></div>
              <div className="review-item"><strong>Phone:</strong> <span>{formData.phone || '—'}</span></div>
              <div className="review-item full-width"><strong>Membership Category:</strong> <span>{categoryLabel}</span></div>
              
              {/* Spouse Member display */}
              {formData.membership_category === 'spouse_member' && (
                <div className="review-item full-width">
                  <strong><i className="fas fa-heart"></i> Relationship:</strong>
                  <span>Spouse of Primary Member (SPE prefix - Full Member)</span>
                </div>
              )}
              
              {formData.membership_category === 'family_member' && relationshipInfo && (
                <div className="review-item full-width">
                  <strong><i className="fas fa-heart"></i> Relationship:</strong>
                  <span>{relationshipInfo.label} ({relationshipInfo.prefix} prefix)</span>
                </div>
              )}
              {formData.membership_category === 'junior_member' && (
                <div className="review-item full-width">
                  <strong><i className="fas fa-child"></i> Relationship:</strong>
                  <span>Child (CHL prefix)</span>
                </div>
              )}
              {prefixPreview && (
                <div className="review-item full-width prefix-preview">
                  <strong><i className="fas fa-id-card"></i> Membership Prefix:</strong>
                  <span className="prefix-badge">{prefixPreview}</span>
                </div>
              )}
              {categoryDesc && <div className="review-item full-width"><strong>Category Info:</strong> <span className="category-desc">{categoryDesc}</span></div>}
              
              {/* Show primary member for spouse as well */}
              {requiresPrimaryMember && (selectedPrimaryMember?.id || formData.primary_member_id) && (
                <div className="review-item full-width success-highlight">
                  <strong><i className="fas fa-check-circle"></i> {formData.membership_category === 'spouse_member' ? 'Spouse/Partner ID' : 'Parent/Guardian ID'}:</strong>
                  <div className="selected-member-review">
                    <span>{formData.membership_category === 'spouse_member' ? 'Partner ID' : 'Parent ID'}: {formData.primary_member_id}</span>
                    {selectedPrimaryMember?.first_name && (
                      <>
                        <span>{selectedPrimaryMember.first_name} {selectedPrimaryMember.last_name}</span>
                        <small>{selectedPrimaryMember.membership_number}</small>
                      </>
                    )}
                  </div>
                </div>
              )}
              {formData.membership_category === 'associate_member' && evidenceFileName && (
                <div className="review-item full-width success-highlight">
                  <strong><i className="fas fa-file-alt"></i> Evidence Document:</strong>
                  <span>{evidenceFileName}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="review-section">
            <div className="review-section-header">
              <i className="fas fa-address-card"></i>
              <h4>Personal Details</h4>
              <button type="button" className="review-edit-btn" onClick={() => setCurrentStep(2)}>
                <i className="fas fa-edit"></i> Edit
              </button>
            </div>
            <div className="review-grid">
              <div className="review-item"><strong>Date of Birth:</strong> <span>{formatDate(formData.date_of_birth)}</span></div>
              <div className="review-item"><strong>Occupation:</strong> <span>{formData.occupation || 'Not provided'}</span></div>
              <div className="review-item full-width"><strong>Address:</strong> <span>{formData.address || 'Not provided'}</span></div>
            </div>
          </div>
          
          <div className="review-section">
            <div className="review-section-header">
              <i className="fas fa-shield-alt"></i>
              <h4>Security</h4>
              <button type="button" className="review-edit-btn" onClick={() => setCurrentStep(3)}>
                <i className="fas fa-edit"></i> Edit
              </button>
            </div>
            <div className="review-grid">
              <div className="review-item"><strong>Password:</strong> <span>••••••••</span></div>
              <div className="review-item full-width"><strong>Terms Accepted:</strong> <span className="success-text"><i className="fas fa-check-circle"></i> Yes</span></div>
            </div>
          </div>
        </div>
        
        <div className="review-warning">
          <i className="fas fa-info-circle"></i>
          <span>By submitting, you confirm that all information provided is accurate and complete.</span>
        </div>
      </div>
    );
  };

  // Determine email validation message
  const getEmailValidationMessage = () => {
    if (!formData.email) return null;
    if (!isEmailComplete(formData.email)) {
      return <small className="info-hint"><i className="fas fa-info-circle"></i> Please enter a complete email address (e.g., name@example.com)</small>;
    }
    if (emailFormatValid === false) {
      return <small className="error-hint"><i className="fas fa-exclamation-triangle"></i> Invalid email format. Please enter a valid email (e.g., name@example.com)</small>;
    }
    if (domainChecking) {
      return <small className="info-hint"><i className="fas fa-globe"></i> Checking email domain...</small>;
    }
    if (domainValid === false && !domainChecking) {
      return <small className="error-hint"><i className="fas fa-exclamation-triangle"></i> This email domain is invalid or cannot receive emails.</small>;
    }
    if (domainSuggestion) {
      return <small className="suggestion-hint"><i className="fas fa-lightbulb"></i> Did you mean <strong>{domainSuggestion}</strong>?</small>;
    }
    if (emailChecking) {
      return <small className="info-hint"><i className="fas fa-spinner fa-spin"></i> Checking availability...</small>;
    }
    if (emailValid === false && !emailChecking) {
      return <small className="error-hint"><i className="fas fa-envelope"></i> This email is already registered. Please use a different email.</small>;
    }
    if (emailValid === true && domainValid === true && emailFormatValid === true && !emailChecking && !domainChecking) {
      return <small className="success-hint"><i className="fas fa-check-circle"></i> Email is valid and available</small>;
    }
    return null;
  };

  // Determine phone validation message
  const getPhoneValidationMessage = () => {
    if (!formData.phone) return null;
    
    const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
    
    if (cleanPhone.length < 8) {
      return <small className="info-hint"><i className="fas fa-info-circle"></i> Please enter a complete phone number with country code (e.g., +1234567890)</small>;
    }
    
    if (cleanPhone.length > 16) {
      return <small className="error-hint"><i className="fas fa-exclamation-triangle"></i> Phone number is too long. Please enter a valid number.</small>;
    }
    
    const internationalPhoneRegex = /^(\+?[0-9]{1,4}[0-9]{5,15}|[0-9]{8,15})$/;
    
    if (!internationalPhoneRegex.test(cleanPhone)) {
      return <small className="error-hint"><i className="fas fa-exclamation-triangle"></i> Invalid phone format. Use international format like +1234567890 or 1234567890</small>;
    }
    
    if (phoneChecking) {
      return <small className="info-hint"><i className="fas fa-spinner fa-spin"></i> Checking availability...</small>;
    }
    
    if (phoneValid === false && !phoneChecking) {
      return <small className="error-hint"><i className="fas fa-phone"></i> This phone number is already registered. Please use a different number.</small>;
    }
    
    if (phoneValid === true && !phoneChecking) {
      return <small className="success-hint"><i className="fas fa-check-circle"></i> Phone number is available</small>;
    }
    
    return null;
  };

  return (
    <AnimatePresence>
      {isPageLoaded && (
        <motion.div className="register-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <div className="register-container">
            <motion.div className="register-form-wrapper-full" variants={containerVariants} initial="hidden" animate="visible">
              <div className="register-card-full">
                <motion.div variants={itemVariants} className="form-header">
                  <motion.div className="header-badge" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
                    <i className="fas fa-user-plus"></i> Join Us Today
                  </motion.div>
                  <h2>Create Account</h2>
                  <p>Register to become a member of NLNG RA Club</p>
                </motion.div>

                {/* Progress Indicator */}
                <div className="progress-container">
                  <div className="progress-steps">
                    {[1, 2, 3, 4].map(step => (
                      <div key={step} className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
                        <div className="step-circle">
                          {currentStep > step ? <i className="fas fa-check"></i> : step}
                        </div>
                        <span className="step-label">
                          {step === 1 ? 'Account' : step === 2 ? 'Details' : step === 3 ? 'Security' : 'Review'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {message && (
                    <motion.div className={`alert-message ${isError ? 'alert-error' : 'alert-success'}`}
                      initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                      <div className="alert-icon"><i className={`fas ${isError ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i></div>
                      <span>{message}</span>
                      <button className="alert-close" onClick={() => setMessage('')}><i className="fas fa-times"></i></button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    {/* STEP 1: Account Information */}
                    {currentStep === 1 && (
                      <motion.div key="step1" className="step-content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label><i className="fas fa-user"></i> First Name *</label>
                            <input type="text" name="first_name" value={formData.first_name} onChange={handleChange}
                              placeholder="Enter first name" disabled={loading} required />
                          </div>
                          <div className="form-group">
                            <label><i className="fas fa-user"></i> Last Name *</label>
                            <input type="text" name="last_name" value={formData.last_name} onChange={handleChange}
                              placeholder="Enter last name" disabled={loading} required />
                          </div>
                        </div>

                        <div className="form-group">
                          <label><i className="fas fa-envelope"></i> Email Address *</label>
                          <div className="input-with-validation">
                            <input 
                              type="email" 
                              name="email" 
                              value={formData.email} 
                              onChange={handleChange}
                              placeholder="name@example.com" 
                              disabled={loading} 
                              required 
                              className={emailFormatValid === false || emailValid === false || domainValid === false ? 'error-input' : (emailValid === true && domainValid === true ? 'success-input' : '')}
                            />
                            {(emailChecking || domainChecking) && <div className="validation-loading"><i className="fas fa-spinner fa-spin"></i></div>}
                            {emailValid === true && domainValid === true && emailFormatValid === true && !emailChecking && !domainChecking && 
                              <div className="validation-success"><i className="fas fa-check-circle"></i></div>
                            }
                            {(emailValid === false || domainValid === false || emailFormatValid === false) && !emailChecking && !domainChecking && 
                              <div className="validation-error"><i className="fas fa-times-circle"></i></div>
                            }
                          </div>
                          {getEmailValidationMessage()}
                        </div>

                        <div className="form-group">
                          <label><i className="fas fa-phone"></i> Phone Number *</label>
                          <div className="input-with-validation">
                            <input 
                              type="tel" 
                              name="phone" 
                              value={formData.phone} 
                              onChange={handleChange}
                              placeholder="+1234567890 or 08012345678" 
                              disabled={loading} 
                              required
                              className={phoneValid === false ? 'error-input' : (phoneValid === true ? 'success-input' : '')}
                            />
                            {phoneChecking && <div className="validation-loading"><i className="fas fa-spinner fa-spin"></i></div>}
                            {phoneValid === true && !phoneChecking && <div className="validation-success"><i className="fas fa-check-circle"></i></div>}
                            {phoneValid === false && !phoneChecking && <div className="validation-error"><i className="fas fa-times-circle"></i></div>}
                          </div>
                          {getPhoneValidationMessage()}
                          <small className="field-hint">Enter with country code (e.g., +1 for USA, +44 for UK, or 080 for Nigeria)</small>
                        </div>
                        
                        <div className="form-group">
                          <label><i className="fas fa-tag"></i> Membership Category *</label>
                          <select name="membership_category" value={formData.membership_category} onChange={handleCategoryChange} disabled={loading} required>
                            <option value="">Select a Category to see details</option>
                            {membershipCategories.map(category => (
                              <option key={category.value} value={category.value}>
                                {category.label} ({category.prefix})
                              </option>
                            ))}
                          </select>
                          {formData.membership_category && (
                            <div className="category-info">
                              <div className="category-description">
                                <i className="fas fa-info-circle"></i>
                                <span>{getCategoryDescription(formData.membership_category)}</span>
                              </div>
                              <div className="category-note">
                                <i className="fas fa-lightbulb"></i>
                                <span>{getCategoryNote(formData.membership_category)}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Relationship Selection for Family Members only */}
                        {formData.membership_category === 'family_member' && (
                          <div className="form-group">
                            <label><i className="fas fa-heart"></i> Relationship to Primary Member *</label>
                            <select 
                              name="relationship" 
                              value={formData.relationship} 
                              onChange={handleChange}
                              required
                              disabled={loading}
                            >
                              <option value="">Select Relationship</option>
                              {relationshipOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label} - {option.prefix} prefix ({option.description})
                                </option>
                              ))}
                            </select>
                            {selectedRelationship && (
                              <div className="relationship-info">
                                <div className="relationship-preview">
                                  <i className={`fas ${selectedRelationship.icon}`}></i>
                                  <span>
                                    You will receive the <strong>{selectedRelationship.prefix}</strong> prefix 
                                    (Example: RACLUB-{selectedRelationship.prefix}-2026-XXXXX)
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Junior Member info */}
                        {formData.membership_category === 'junior_member' && (
                          <div className="form-group">
                            <div className="relationship-info">
                              <div className="relationship-preview">
                                <i className="fas fa-child"></i>
                                <span>
                                  Junior members receive the <strong>CHL</strong> prefix 
                                  (Example: RACLUB-CHL-2026-XXXXX)
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Prefix Preview for all categories */}
                        {prefixPreview && formData.membership_category && (
                          <div className="form-group">
                            <div className="prefix-preview-box">
                              <i className="fas fa-id-card"></i>
                              <span>Your Membership Prefix: <strong>{prefixPreview}</strong></span>
                            </div>
                          </div>
                        )}

                        {/* Evidence Upload for Associate Members */}
                        {formData.membership_category === 'associate_member' && (
                          <div className="form-group evidence-group">
                            <label><i className="fas fa-file-upload"></i> Supporting Evidence *</label>
                            <div className="evidence-upload-area">
                              <input
                                type="file"
                                id="evidence"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleEvidenceFileChange}
                                disabled={loading}
                                style={{ display: 'none' }}
                              />
                              <button
                                type="button"
                                className="evidence-upload-btn"
                                onClick={() => document.getElementById('evidence').click()}
                                disabled={loading}
                              >
                                <i className="fas fa-cloud-upload-alt"></i>
                                {evidenceFileName ? 'Change File' : 'Upload Evidence'}
                              </button>
                              {evidenceFileName && (
                                <div className="evidence-file-info">
                                  <i className="fas fa-file"></i>
                                  <span>{evidenceFileName}</span>
                                  <button
                                    type="button"
                                    className="remove-evidence"
                                    onClick={() => { setEvidenceFile(null); setEvidenceFileName(''); }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              )}
                              <small className="field-hint">Accepted formats: PDF, JPG, PNG (Max 5MB)</small>
                            </div>
                          </div>
                        )}

                        {/* PRIMARY MEMBER SELECTION - Shows for spouse, junior, and family members */}
                        {requiresPrimaryMember && (
                          <div className="form-group primary-member-group">
                            <label><i className="fas fa-user-check"></i> {formData.membership_category === 'spouse_member' ? 'Spouse/Partner (Primary Member) *' : 'Parent/Guardian (Primary Member) *'}</label>
                            <div className="primary-search-wrapper" ref={primarySearchRef}>
                              {selectedPrimaryMember && selectedPrimaryMember.id ? (
                                <div className="selected-member-success-card">
                                  <div className="success-card-header">
                                    <div className="success-icon-wrapper">
                                      <i className="fas fa-check-circle"></i>
                                    </div>
                                    <span className="success-badge">✓ {formData.membership_category === 'spouse_member' ? 'Partner Selected' : 'Parent/Guardian Selected'}</span>
                                  </div>
                                  <div className="selected-member-details-visible">
                                    <div className="member-avatar">
                                      <i className="fas fa-user-circle"></i>
                                    </div>
                                    <div className="member-info-visible">
                                      <strong className="member-name">{selectedPrimaryMember.first_name} {selectedPrimaryMember.last_name}</strong>
                                      <span className="member-number">{selectedPrimaryMember.membership_number}</span>
                                      <span className="member-email-visible">{selectedPrimaryMember.email}</span>
                                    </div>
                                    <button type="button" onClick={clearSelectedPrimaryMember} className="change-member-btn">
                                      <i className="fas fa-exchange-alt"></i> Change
                                    </button>
                                  </div>
                                  <div className="selected-member-note-visible">
                                    <i className="fas fa-info-circle"></i>
                                    {formData.membership_category === 'spouse_member' 
                                      ? 'This member will be linked as your spouse/partner. You will have full member privileges.'
                                      : 'This member will be linked as your parent/guardian.'}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="search-input-container">
                                    <input 
                                      type="text" 
                                      value={primaryMemberSearch} 
                                      onChange={(e) => setPrimaryMemberSearch(e.target.value)}
                                      placeholder="Search by name or membership number..." 
                                      disabled={loading} 
                                      className="primary-search-input" 
                                    />
                                    {primaryMemberLoading && <div className="search-loading"><i className="fas fa-spinner fa-spin"></i></div>}
                                  </div>
                                  
                              {showPrimaryDropdown && (
  <div className="primary-dropdown">
    {primaryMemberLoading ? (
      <div className="primary-dropdown-loading">
        <i className="fas fa-spinner fa-spin"></i> Searching...
      </div>
    ) : primaryMemberResults.length > 0 ? (
      primaryMemberResults.map(member => (
        <div key={member.id} className="primary-dropdown-item" onClick={() => selectPrimaryMember(member)}>
          <div className="dropdown-member-info">
            <strong>{member.first_name} {member.last_name}</strong>
            <span className="member-number">{member.membership_number}</span>
          </div>
          <small className="member-email">{member.email}</small>
          <span className="member-type-badge">{member.membership_type || 'primary'}</span>
        </div>
      ))
    ) : hasSearched && primaryMemberSearch.length >= 2 ? (
      <div className="primary-dropdown-empty">
        <i className="fas fa-user-slash"></i>
        <strong>No members found</strong>
        <span>Could not find "{primaryMemberSearch}"</span>
        <small>Check the spelling or try a different search term</small>
      </div>
    ) : (
      <div className="primary-dropdown-hint">
        <i className="fas fa-search"></i>
        <span>Type at least 2 characters to search</span>
      </div>
    )}
  </div>
)}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* STEP 2: Personal Details */}
                    {currentStep === 2 && (
                      <motion.div key="step2" className="step-content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label><i className="fas fa-calendar-alt"></i> Date of Birth</label>
                            <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} disabled={loading} />
                          </div>
                          <div className="form-group">
                            <label><i className="fas fa-briefcase"></i> Occupation</label>
                            <input type="text" name="occupation" value={formData.occupation} onChange={handleChange} placeholder="Your occupation" disabled={loading} />
                          </div>
                        </div>
                        <div className="form-group">
                          <label><i className="fas fa-map-marker-alt"></i> Address</label>
                          <textarea name="address" value={formData.address} onChange={handleChange} placeholder="Your full address" rows="3" disabled={loading}></textarea>
                        </div>
                      </motion.div>
                    )}

                    {/* STEP 3: Security */}
                    {currentStep === 3 && (
                      <motion.div key="step3" className="step-content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label><i className="fas fa-lock"></i> Password *</label>
                            <div className="password-input-wrapper">
                              <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange}
                                placeholder="Create a password" disabled={loading} required />
                              <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)}>
                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                            <small className="field-hint">Minimum 6 characters</small>
                          </div>
                          <div className="form-group">
                            <label><i className="fas fa-lock"></i> Confirm Password *</label>
                            <div className="password-input-wrapper">
                              <input type={showConfirmPassword ? "text" : "password"} name="confirm_password" value={formData.confirm_password} onChange={handleChange}
                                placeholder="Confirm your password" disabled={loading} required />
                              <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="form-terms">
                          <label className="checkbox-label">
                            <input type="checkbox" checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} disabled={loading} />
                            <span className="checkmark"></span>
                            <span>I agree to the <Link to="/terms" className="link">Terms of Service</Link> and <Link to="/privacy" className="link">Privacy Policy</Link></span>
                          </label>
                        </div>
                      </motion.div>
                    )}

                    {/* STEP 4: Review & Confirm */}
                    {currentStep === 4 && <ReviewStep />}
                  </AnimatePresence>

                  {/* Navigation Buttons */}
                  <div className="form-navigation">
                    {currentStep > 1 && (
                      <button type="button" className="nav-btn prev-btn" onClick={prevStep} disabled={loading}>
                        <i className="fas fa-arrow-left"></i> Back
                      </button>
                    )}
                    {currentStep < totalSteps ? (
                      <button type="button" className="nav-btn next-btn" onClick={nextStep} disabled={loading}>
                        Continue <i className="fas fa-arrow-right"></i>
                      </button>
                    ) : (
                      <button type="submit" className="register-btn" disabled={loading}>
                        {loading ? <><div className="spinner"></div> Creating Account...</> : <>Confirm & Create Account <i className="fas fa-check-circle"></i></>}
                      </button>
                    )}
                  </div>
                </form>

                <div className="divider"><span>Already have an account?</span></div>
                <Link to="/login" className="login-link"><i className="fas fa-sign-in-alt"></i> Sign In to Your Account</Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RegisterPage;